/** Traffic-light evaluation for the device Security tab. */

import { SECURITY_SECTIONS } from '@/lib/securityScripts'
import { isExcluded, normaliseExclusions } from '@/lib/postureExclusions'
import { detectDotNet6FromPayload } from '@/lib/dotnetDetection'
import { buildPosture, mapSectionResults, worstStatus } from '@/lib/trafficLight'

export { TRAFFIC } from '@/lib/trafficLight'

const HEADLINES = {
  green: 'All clear — security checks look good',
  amber: 'Review recommended — some areas need a look',
  red: 'Action needed — one or more areas require attention',
  grey: 'No security data yet for this device',
}

function isOff(value) {
  if (value === false) return true
  if (value == null || value === '') return false
  const text = String(value).trim().toLowerCase()
  return text === 'off' || text === 'disabled' || text === 'false' || text === 'no'
}

function isOn(value) {
  if (value === true) return true
  if (value == null || value === '') return false
  const text = String(value).trim().toLowerCase()
  return text === 'on' || text === 'enabled' || text === 'true' || text === 'yes' || text === 'active'
}

function snapshotBySlug(snapshots, slug) {
  return snapshots.find((s) => s.script_slug === slug) ?? null
}

/** ThreatDown is the AV/EDR — Defender inactive/disabled is expected. */
function threatdownCoversDefender(threatdown) {
  if (!threatdown) return false
  const protection = String(threatdown.protection_status ?? '').toLowerCase()
  return protection === 'active' || protection === 'protected'
}

const DEFENDER_BUILTIN_PRODUCTS = new Set([
  'windows defender',
  'windows security',
  'microsoft defender for endpoint',
])

/** Inventory third-party AV — Defender inactive/disabled is expected. */
export function thirdPartyAvCoversDefender(avProduct, avStatus) {
  if (String(avStatus ?? '').trim().toLowerCase() !== 'protected') return false
  const product = String(avProduct ?? '').trim()
  if (!product) return false
  return !DEFENDER_BUILTIN_PRODUCTS.has(product.toLowerCase())
}

function evaluateThreatProtection({
  snapshots,
  threatdown,
  avProduct = null,
  avStatus = null,
  exclusions = {},
}) {
  const issues = []
  let status = 'grey'
  const defender = snapshotBySlug(snapshots, 'defender-dns')?.payload ?? {}
  const hasDefender = Object.keys(defender).length > 0
  const covered =
    threatdownCoversDefender(threatdown) || thirdPartyAvCoversDefender(avProduct, avStatus)

  if (hasDefender) {
    status = 'green'
    const threats = String(defender.defender_threats ?? '').toUpperCase()
    if (threats.includes('THREAT') && !threats.includes('CLEAN')) {
      issues.push({ text: 'Defender reported threats', status: 'red' })
      status = 'red'
    }
    if (!covered) {
      if (isOff(defender.defender_realtime)) {
        issues.push({ text: 'Real-time protection is off', status: 'red' })
        status = 'red'
      }
      if (isOff(defender.defender_antivirus)) {
        issues.push({ text: 'Antivirus is disabled', status: 'red' })
        status = worstStatus(status, 'red')
      }
      const defenderStatus = String(defender.defender_status ?? '').toLowerCase()
      if (
        defenderStatus &&
        !['active', 'enabled', 'ok', 'healthy', 'running'].some((x) =>
          defenderStatus.includes(x),
        )
      ) {
        issues.push({ text: 'Defender status needs review', status: 'amber' })
        status = worstStatus(status, 'amber')
      }
    }
  }

  if (threatdown) {
    status = status === 'grey' ? 'green' : status
    const protection = String(threatdown.protection_status ?? '').toLowerCase()
    if (protection && !['active', 'protected'].includes(protection)) {
      issues.push({ text: 'ThreatDown protection is not active', status: 'amber' })
      status = worstStatus(status, 'amber')
    }
    if (threatdown.online === false) {
      const lastSeen = threatdown.last_seen_at ? new Date(threatdown.last_seen_at) : null
      const fourWeeksMs = 4 * 7 * 24 * 60 * 60 * 1000
      const stale =
        !lastSeen ||
        Number.isNaN(lastSeen.getTime()) ||
        Date.now() - lastSeen.getTime() > fourWeeksMs
      if (stale) {
        issues.push({
          text:
            lastSeen && !Number.isNaN(lastSeen.getTime())
              ? 'ThreatDown agent offline for over 4 weeks'
              : 'ThreatDown agent is offline (last seen unknown)',
          status: 'amber',
        })
        status = worstStatus(status, 'amber')
      }
    }
  }

  if (!isExcluded(exclusions, 'office_macros')) {
    const macros = snapshotBySlug(snapshots, 'office-macros')?.payload ?? {}
    if (Object.keys(macros).length > 0) {
      status = status === 'grey' ? 'green' : status
      if (macros.macros_allowed === true) {
        issues.push({ text: 'Office macros are allowed to run', status: 'red' })
        status = worstStatus(status, 'red')
      } else if (macros.macros_allowed === false) {
        status = status === 'grey' ? 'green' : status
      } else {
        issues.push({ text: 'Office macro policy not found', status: 'amber' })
        status = worstStatus(status, 'amber')
      }
    }
  }

  return { id: 'threat-protection', status, issues }
}

function evaluateNetwork({ snapshots, exclusions = {} }) {
  const issues = []
  let status = 'grey'
  const firewall = snapshotBySlug(snapshots, 'windows-firewall')?.payload ?? {}
  const defender = snapshotBySlug(snapshots, 'defender-dns')?.payload ?? {}
  const hasDns = Boolean(defender.dns1 || defender.dns2)

  if (Object.keys(firewall).length > 0 && !isExcluded(exclusions, 'windows_firewall')) {
    status = 'green'
    const profiles = Array.isArray(firewall.profiles) ? firewall.profiles : []
    const rows =
      profiles.length > 0
        ? profiles
        : [
            { name: 'Domain', enabled: isOn(firewall.domain_profile) },
            { name: 'Private', enabled: isOn(firewall.private_profile) },
            { name: 'Public', enabled: isOn(firewall.public_profile) },
          ]
    const offProfiles = rows.filter((p) => p.enabled === false)
    if (offProfiles.length > 0) {
      issues.push({
        text: `Firewall off: ${offProfiles.map((p) => p.name).join(', ')}`,
        status: 'red',
      })
      status = 'red'
    }
  }

  if (hasDns && status === 'grey') {
    status = 'green'
  }

  return { id: 'network', status, issues }
}

function evaluateEncryption({ snapshots, exclusions = {} }) {
  const issues = []
  let status = 'grey'
  const bitlocker = snapshotBySlug(snapshots, 'bitlocker')?.payload ?? {}
  const tpm = snapshotBySlug(snapshots, 'tpm-secureboot')?.payload ?? {}

  if (Object.keys(bitlocker).length > 0 && !isExcluded(exclusions, 'bitlocker')) {
    status = bitlocker.system_drive_encrypted === true ? 'green' : 'red'
    if (bitlocker.system_drive_encrypted !== true) {
      issues.push({ text: 'System drive is not BitLocker encrypted', status: 'red' })
    }
  }

  if (Object.keys(tpm).length > 0 && !isExcluded(exclusions, 'tpm_secure_boot')) {
    const tpmStatus = status === 'grey' ? 'green' : status
    status = tpmStatus
    let tpmIssueCount = 0
    if (tpm.tpm_present === false) {
      issues.push({ text: 'TPM not present', status: 'amber' })
      tpmIssueCount += 1
    }
    if (tpm.tpm_ready === false) {
      issues.push({ text: 'TPM not ready', status: 'amber' })
      tpmIssueCount += 1
    }
    if (tpm.tpm_enabled === false) {
      issues.push({ text: 'TPM not enabled', status: 'amber' })
      tpmIssueCount += 1
    }
    if (tpm.secure_boot_enabled === false) {
      issues.push({ text: 'Secure Boot disabled', status: 'amber' })
      tpmIssueCount += 1
    }
    if (tpm.uefi === false) {
      issues.push({ text: 'Legacy boot (not UEFI)', status: 'amber' })
      tpmIssueCount += 1
    }
    if (tpmIssueCount > 0) {
      status = worstStatus(status, 'amber')
    }
  }

  return { id: 'encryption-boot', status, issues }
}

function evaluateSystemHealth({ snapshots, exclusions = {} }) {
  const issues = []
  let status = 'grey'

  // Pending reboot and Toolkit health live on Overview only.
  if (!isExcluded(exclusions, 'dotnet6')) {
    const installedSoftware = snapshotBySlug(snapshots, 'installed-software')?.payload ?? {}
    const dotnet6 = detectDotNet6FromPayload(installedSoftware)
    if (dotnet6.hasData) {
      status = 'green'
      if (dotnet6.installed) {
        issues.push({ text: '.NET 6 (EOL) runtime installed', status: 'red' })
        status = worstStatus(status, 'red')
      }
    }
  }

  return { id: 'system-health', status, issues }
}

function evaluateAccess({ snapshots, events }) {
  const issues = []
  let status = 'grey'
  const admins = snapshotBySlug(snapshots, 'local-admins')?.payload ?? {}
  // Failed logins stay on the Security tab as events only — they must not drive
  // Overview "Action needed" / estate security_status (noisy false positives).
  const adminChanges = events.filter((e) => e.script_slug === 'local-admin-changes')

  if (Object.keys(admins).length > 0) {
    status = 'green'
  }

  if (adminChanges.length > 0) {
    issues.push({
      text: `${adminChanges.length} local admin change(s) today`,
      status: 'amber',
    })
    status = worstStatus(status === 'grey' ? 'green' : status, 'amber')
  }

  return { id: 'access', status, issues }
}

export function evaluateSecurityPosture({
  snapshots = [],
  events = [],
  threatdown = null,
  avProduct = null,
  avStatus = null,
  exclusions = null,
}) {
  const excl = normaliseExclusions(exclusions)
  const evaluators = [
    evaluateThreatProtection({
      snapshots,
      threatdown,
      avProduct,
      avStatus,
      exclusions: excl,
    }),
    evaluateNetwork({ snapshots, exclusions: excl }),
    evaluateEncryption({ snapshots, exclusions: excl }),
    evaluateSystemHealth({ snapshots, exclusions: excl }),
    evaluateAccess({ snapshots, events }),
  ]

  const sections = mapSectionResults(SECURITY_SECTIONS, evaluators)
  return buildPosture({ sections, sectionDefs: SECURITY_SECTIONS, headlines: HEADLINES })
}
